import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import Toast from '../../components/Toast';

interface ContactInfo {
  first_name?: string;
  last_name?: string;
  middle_name?: string;
  email?: string;
  phone?: string;
  alternate_email?: string;
  alternate_phone?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
  linkedin_url?: string;
  github_url?: string;
  portfolio_url?: string;
  twitter_url?: string;
  personal_website?: string;
  confidence?: number;
}

interface ProfessionalSummary {
  summary?: string;
  objective?: string;
  current_title?: string;
  years_experience?: number;
  career_level?: string;
  availability?: string;
  work_type_preference?: string;
  willing_to_relocate?: boolean;
  work_authorization?: string;
  expected_salary?: number;
  salary_currency?: string;
  confidence?: number;
}

interface WorkExperience {
  company_name?: string;
  job_title?: string;
  department?: string;
  employment_type?: string;
  location?: string;
  is_remote?: boolean;
  start_date?: string;
  end_date?: string;
  is_current?: boolean;
  duration_text?: string;
  description?: string;
  responsibilities?: string[];
  achievements?: string[];
  projects?: string[];
  technologies?: string[];
  skills_used?: string[];
  team_size?: number;
  reports?: number;
  metrics?: string[];
  confidence?: number;
}

interface Education {
  institution_name?: string;
  institution_type?: string;
  location?: string;
  degree_type?: string;
  degree_name?: string;
  field_of_study?: string;
  major?: string;
  minor?: string;
  concentration?: string;
  gpa?: number;
  gpa_scale?: string;
  honors?: string[];
  start_date?: string;
  end_date?: string;
  graduation_year?: number;
  is_current?: boolean;
  thesis_title?: string;
  relevant_coursework?: string[];
  activities?: string[];
  projects?: string[];
  confidence?: number;
}

interface Skill {
  name?: string;
  category?: string;
  proficiency?: string;
  years_experience?: number;
  last_used?: string;
  context?: string[];
  mentioned_count?: number;
  confidence?: number;
}

interface Certification {
  name?: string;
  issuing_organization?: string;
  issue_date?: string;
  expiration_date?: string;
  credential_id?: string;
  credential_url?: string;
  does_not_expire?: boolean;
  confidence?: number;
}

interface Language {
  language?: string;
  proficiency?: string;
  proficiency_description?: string;
  is_native?: boolean;
  confidence?: number;
}

interface Project {
  name?: string;
  description?: string;
  role?: string;
  technologies?: string[];
  url?: string;
  start_date?: string;
  end_date?: string;
  is_ongoing?: boolean;
  achievements?: string[];
  confidence?: number;
}

interface Award {
  title?: string;
  organization?: string;
  date?: string;
  description?: string;
  confidence?: number;
}

interface ResumeAnalysis {
  overall_score?: number;
  formatting_score?: number;
  completeness_score?: number;
  strengths?: string[];
  weaknesses?: string[];
  recommendations?: string[];
  red_flags?: string[];
  keyword_density?: Record<string, number>;
  estimated_experience_level?: string;
  industry_alignment?: string;
  sector_classifications?: Array<{
    sector_name?: string;
    subsector?: string;
    confidence?: number;
    reasoning?: string;
    keywords_matched?: string[];
  }>;
  primary_sector?: string;
  missing_sections?: string[];
  inconsistencies?: string[];
}

interface AIProfileData {
  contact_info?: ContactInfo;
  professional_summary?: ProfessionalSummary;
  work_experience?: WorkExperience[];
  education?: Education[];
  skills?: Skill[];
  certifications?: Certification[];
  languages?: Language[];
  projects?: Project[];
  awards?: Award[];
  analysis?: ResumeAnalysis;
  processing_metadata?: Record<string, any>;
  overall_confidence?: number;
}

type OnboardingStep = 'welcome' | 'upload' | 'processing' | 'review' | 'success';

const CandidateOnboarding: React.FC = () => {
  const { user, updateUserState } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialStep = (searchParams.get('step') || 'welcome') as OnboardingStep;
  
  const [currentStep, setCurrentStep] = useState<OnboardingStep>(initialStep);
  const [aiProfileData, setAiProfileData] = useState<AIProfileData | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<string>('');
  const [uploadError, setUploadError] = useState<string>('');
  const [toastMessage, setToastMessage] = useState<{
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
    isVisible: boolean;
  }>({ message: '', type: 'info', isVisible: false });

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Restore processing state from sessionStorage on component mount
  useEffect(() => {
    const restoreProcessingState = () => {
      try {
        const savedState = sessionStorage.getItem('candidate_onboarding_state');
        if (savedState) {
          const parsedState = JSON.parse(savedState);
          console.log('🔄 Restoring processing state:', parsedState);
          
          if (parsedState.currentStep) {
            setCurrentStep(parsedState.currentStep);
          }
          
          if (parsedState.isProcessing && parsedState.taskId) {
            setIsProcessing(true);
            setProcessingStatus(parsedState.processingStatus || 'Resuming resume processing...');
            // Resume polling the task
            setTimeout(() => pollTaskStatus(parsedState.taskId), 1000);
          }
          
          if (parsedState.aiProfileData) {
            setAiProfileData(parsedState.aiProfileData);
          }
        }
      } catch (error) {
        console.error('Error restoring processing state:', error);
        // Clear corrupted state
        sessionStorage.removeItem('candidate_onboarding_state');
      }
    };

    restoreProcessingState();
  }, []);

  // Save processing state to sessionStorage whenever it changes
  const saveProcessingState = (step: OnboardingStep, processing: boolean, taskId?: string, status?: string, profileData?: AIProfileData | null) => {
    try {
      const stateToSave = {
        currentStep: step,
        isProcessing: processing,
        taskId: taskId,
        processingStatus: status,
        aiProfileData: profileData,
        timestamp: Date.now()
      };
      
      sessionStorage.setItem('candidate_onboarding_state', JSON.stringify(stateToSave));
      console.log('💾 Saved processing state:', stateToSave);
    } catch (error) {
      console.error('Error saving processing state:', error);
    }
  };

  // Clear processing state from sessionStorage
  const clearProcessingState = () => {
    sessionStorage.removeItem('candidate_onboarding_state');
    console.log('🧹 Cleared processing state');
  };

  useEffect(() => {
    // Check if user already has a profile - if so, redirect to dashboard
    const checkProfile = async (retryCount: number = 0) => {
      try {
        const result = await apiService.checkCandidateProfileExists();
        if (result.has_profile && (result.profile_type === 'self_owned' || result.profile_type === 'job_seeker')) {
          clearProcessingState(); // Clear state before redirecting
          navigate('/candidate/dashboard', { replace: true });
        }
        // For company-owned profiles that can be claimed, stay on onboarding
      } catch (error: any) {
        console.error('Error checking profile:', error);
        
        // Handle authentication errors with retry logic
        if ((error.message?.includes('Not authenticated') || error.message?.includes('403')) && retryCount < 1) {
          console.log('🔄 Authentication error checking profile, retrying in 2 seconds...');
          setTimeout(() => checkProfile(retryCount + 1), 2000);
          return;
        }
        
        // If retry failed, don't redirect - let user continue with onboarding
        console.warn('⚠️ Could not check existing profile, continuing with onboarding');
      }
    };

    if (currentStep !== 'welcome' && currentStep !== 'review' && currentStep !== 'success') {
      checkProfile();
    }
  }, [navigate, currentStep]);

  const showToast = (message: string, type: 'success' | 'error' | 'warning' | 'info') => {
    setToastMessage({ message, type, isVisible: true });
  };

  const hideToast = () => {
    setToastMessage(prev => ({ ...prev, isVisible: false }));
  };

  const handleResumeUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    
    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      setUploadError('Please upload a PDF or Word document.');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('File size must be less than 5MB.');
      return;
    }

    setCurrentStep('processing');
    setIsProcessing(true);
    setUploadError('');
    setProcessingStatus('Uploading your resume...');

    try {
      const response = await apiService.createCandidateProfileFromResume(file);
      
      if (response.success && response.task_id) {
        setProcessingStatus('Analyzing your resume with AI...');
        
        // Save processing state with task ID
        saveProcessingState('processing', true, response.task_id, 'Analyzing your resume with AI...');
        
        pollTaskStatus(response.task_id);
      } else {
        throw new Error(response.message || 'Failed to start resume processing');
      }
      
    } catch (error: any) {
      setUploadError(error.message || 'Failed to process resume');
      setCurrentStep('upload');
      setIsProcessing(false);
      setProcessingStatus('');
      clearProcessingState(); // Clear state on error
      showToast('Failed to upload resume. Please try again.', 'error');
    }
  };

  const pollTaskStatus = async (taskId: string, retryCount: number = 0) => {
    try {
      const statusResponse = await apiService.getCandidateProfileCreationStatus(taskId);
      
      if (statusResponse.status === 'completed') {
        setIsProcessing(false);
        setProcessingStatus('');
        
        if (statusResponse.result && statusResponse.result.extracted_data) {
          console.log('✅ Resume processing completed, setting AI profile data');
          console.log('📊 Extracted data structure:', statusResponse.result.extracted_data);
          setAiProfileData(statusResponse.result.extracted_data);
          setCurrentStep('review'); // Stay on review step, don't auto-redirect to success
          clearProcessingState();
          showToast('Resume processed successfully! Please review your profile.', 'success');
        } else if (statusResponse.result) {
          // Fallback: use result directly if extracted_data is not nested
          console.log('✅ Resume processing completed, using result directly');
          setAiProfileData(statusResponse.result);
          setCurrentStep('review');
          clearProcessingState();
          showToast('Resume processed successfully! Please review your profile.', 'success');
        } else {
          setUploadError('Resume processing completed but no profile data received');
          setCurrentStep('upload');
          clearProcessingState();
        }
        
      } else if (statusResponse.status === 'failed') {
        setIsProcessing(false);
        setProcessingStatus('');
        setUploadError(statusResponse.error || 'Resume processing failed');
        setCurrentStep('upload');
        clearProcessingState(); // Clear state on failure
        showToast('Resume processing failed. Please try again.', 'error');
        
      } else if (statusResponse.status === 'processing') {
        const meta = statusResponse.meta || {};
        let newStatus = 'Processing your resume...';
        
        if (meta.status === 'processing_resume') {
          newStatus = 'Extracting information from your resume...';
        } else if (meta.status === 'creating_profile') {
          newStatus = 'Creating your professional profile...';
        } else if (meta.status === 'uploading_file') {
          newStatus = 'Saving your resume securely...';
        }
        
        setProcessingStatus(newStatus);
        
        // Update processing state
        saveProcessingState('processing', true, taskId, newStatus);
        
        setTimeout(() => pollTaskStatus(taskId), 2000);
        
      } else {
        const newStatus = 'Your resume is in the processing queue...';
        setProcessingStatus(newStatus);
        
        // Update processing state
        saveProcessingState('processing', true, taskId, newStatus);
        
        setTimeout(() => pollTaskStatus(taskId), 2000);
      }
      
    } catch (error: any) {
      console.error('Error polling task status:', error);
      
      // Handle authentication errors with retry logic
      if ((error.message?.includes('Not authenticated') || error.message?.includes('403')) && retryCount < 2) {
        console.log(`🔄 Authentication error during polling, retrying in 3 seconds... (attempt ${retryCount + 1}/2)`);
        setProcessingStatus('Reconnecting... Please wait.');
        
        // Wait a bit longer to allow token refresh to complete
        setTimeout(() => pollTaskStatus(taskId, retryCount + 1), 3000);
        return;
      }
      
      // If retry failed or other error, fail gracefully
      setIsProcessing(false);
      setProcessingStatus('');
      setUploadError('Failed to check processing status. Please try uploading again.');
      setCurrentStep('upload');
      clearProcessingState(); // Clear state on error
      showToast('Processing failed. Please try again.', 'error');
    }
  };

  const handleConfirmProfile = async () => {
    try {
      setIsProcessing(true);
      
      // Transform AI profile data to match backend expectations
      if (aiProfileData) {
        const profileUpdateData = {
          // Extract contact information
          first_name: aiProfileData.contact_info?.first_name || '',
          last_name: aiProfileData.contact_info?.last_name || '',
          phone: aiProfileData.contact_info?.phone || '',
          location: aiProfileData.contact_info?.address || 
                   (aiProfileData.contact_info?.city && aiProfileData.contact_info?.state 
                    ? `${aiProfileData.contact_info.city}, ${aiProfileData.contact_info.state}` 
                    : aiProfileData.contact_info?.city || ''),
          
          // Extract professional summary as string, not object
          professional_summary: aiProfileData.professional_summary?.summary || 
                               aiProfileData.professional_summary?.objective || '',
          
          // Extract current position from first work experience
          current_job_title: aiProfileData.work_experience?.[0]?.job_title || '',
          current_company: aiProfileData.work_experience?.[0]?.company_name || '',
          
          // Extract years of experience
          years_of_experience: aiProfileData.professional_summary?.years_experience || 0,
          
          // Extract work preferences
          expected_salary: aiProfileData.professional_summary?.expected_salary || null,
          availability: aiProfileData.professional_summary?.availability || '',
          preferred_work_type: aiProfileData.professional_summary?.work_type_preference || '',
          willing_to_relocate: aiProfileData.professional_summary?.willing_to_relocate || false,
          
          // Social profiles from contact info
          linkedin_url: aiProfileData.contact_info?.linkedin_url || '',
          github_url: aiProfileData.contact_info?.github_url || '',
          portfolio_url: aiProfileData.contact_info?.portfolio_url || '',
          personal_website: aiProfileData.contact_info?.personal_website || '',
          twitter_url: aiProfileData.contact_info?.twitter_url || ''
        };
        
        // Remove empty strings to avoid overwriting existing data with empty values
        const cleanedData = Object.fromEntries(
          Object.entries(profileUpdateData).filter(([_, value]) => 
            value !== null && value !== undefined
          )
        );
        
        await apiService.updateCandidateProfile(cleanedData);

        // Update React user state so welcome header re-renders immediately
        updateUserState({
          first_name: profileUpdateData.first_name || user?.first_name || '',
          last_name: profileUpdateData.last_name || user?.last_name || '',
        });
      }
      
      clearProcessingState();
      sessionStorage.setItem('profile_creation_completed', 'true');
      navigate('/candidate/dashboard', { replace: true });
      
    } catch (error: any) {
      console.error('Error saving profile:', error);
      showToast('Failed to save profile. Please try again.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleSkipOnboarding = () => {
    // Set a flag to indicate user has seen onboarding
    sessionStorage.setItem('skip_onboarding', 'true');
    clearProcessingState(); // Clear any processing state when skipping
    // Allow users to skip and go directly to dashboard
    navigate('/candidate/dashboard', { replace: true });
  };

  // Enhanced helper function to safely render any value with proper type checking
  const safeRender = (value: any, fallback: string = 'Not provided'): string => {
    if (value === null || value === undefined || value === '') return fallback;
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(', ') : fallback;
    }
    if (typeof value === 'object') {
      // Handle nested object types by extracting meaningful information
      if (value.summary) return value.summary;
      if (value.objective) return value.objective;
      if (value.name) return value.name;
      if (value.title) return value.title;
      // Only stringify as last resort
      try {
        return JSON.stringify(value);
      } catch {
        return fallback;
      }
    }
    return String(value);
  };

  // Enhanced helper function to extract professional summary
  const getProfessionalSummary = (summary: ProfessionalSummary | null | undefined): string => {
    if (!summary) return 'No professional summary available';
    if (summary.summary) return summary.summary;
    if (summary.objective) return summary.objective;
    return 'Professional summary extracted from resume';
  };

  // Enhanced helper function to get location info
  const getLocationInfo = (): string => {
    if (!aiProfileData?.contact_info) return 'Not provided';
    
    const contact = aiProfileData.contact_info;
    
    // Try different location field combinations
    if (contact.address) return contact.address;
    
    // Construct from city and state
    const locationParts = [];
    if (contact.city) locationParts.push(contact.city);
    if (contact.state) locationParts.push(contact.state);
    if (locationParts.length > 0) return locationParts.join(', ');
    
    // Try other location fields
    if (contact.country) return contact.country;
    
    return 'Not provided';
  };

  // Helper function to safely get contact info
  const getContactInfo = (field: keyof ContactInfo): string => {
    return safeRender(aiProfileData?.contact_info?.[field]);
  };

  // Helper function to safely get array length
  const getArrayLength = (arr: any[] | undefined): number => {
    return Array.isArray(arr) ? arr.length : 0;
  };

  const renderWelcomeStep = () => (
    <div className="text-center max-w-2xl mx-auto">
      <div className="mb-8">
        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-emerald-100 mb-6">
          <svg className="h-8 w-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          Welcome to TalentKey.AI, {user?.first_name}!
        </h1>
        <p className="text-xl text-gray-600 mb-8">
          Let's create your professional profile so top companies can discover you
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
              <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Upload Resume</h3>
            <p className="text-sm text-gray-600">Share your resume and we'll extract all the important details</p>
          </div>
          
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-purple-100 mb-4">
              <svg className="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">AI Analysis</h3>
            <p className="text-sm text-gray-600">Our AI will analyze your experience, skills, and education</p>
          </div>
          
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Review & Confirm</h3>
            <p className="text-sm text-gray-600">Review the extracted information and make any adjustments</p>
          </div>
        </div>

        <div className="space-y-4">
          <button
            onClick={() => setCurrentStep('upload')}
            className="w-full flex justify-center py-4 px-6 border border-transparent rounded-lg shadow-sm text-lg font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition duration-150 ease-in-out"
          >
            Get Started
          </button>
          
          <button
            onClick={handleSkipOnboarding}
            className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition duration-150 ease-in-out"
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );

  const renderUploadStep = () => (
    <div className="text-center max-w-xl mx-auto">
      <div className="mb-8">
        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-6">
          <svg className="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Upload Your Resume</h2>
        <p className="text-lg text-gray-600">
          Upload your resume and our AI will automatically create your professional profile
        </p>
      </div>

      {uploadError && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
            {uploadError}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-emerald-400 transition-colors">
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx"
            onChange={(e) => handleResumeUpload(e.target.files)}
            className="hidden"
          />
          
          <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" stroke="currentColor" fill="none" viewBox="0 0 48 48">
            <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          
          <div className="text-center">
            <button
              onClick={handleUploadClick}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
            >
              Choose File
            </button>
            <p className="mt-2 text-sm text-gray-600">or drag and drop your resume here</p>
          </div>
          
          <p className="text-xs text-gray-500 mt-4">
            Supports PDF, DOC, DOCX files up to 5MB
          </p>
        </div>
      </div>

      <div className="flex justify-between">
        <button
          onClick={() => setCurrentStep('welcome')}
          className="flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        
        <button
          onClick={handleSkipOnboarding}
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          Skip for now
        </button>
      </div>
    </div>
  );

  const renderProcessingStep = () => (
    <div className="text-center max-w-xl mx-auto">
      <div className="mb-8">
        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-purple-100 mb-6">
          <svg className="animate-spin h-8 w-8 text-purple-600" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Processing Your Resume</h2>
        <p className="text-lg text-gray-600 mb-4">
          Our AI is analyzing your resume and creating your professional profile
        </p>
        <p className="text-sm text-emerald-600 font-medium">
          {processingStatus}
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-8">
        <div className="space-y-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Uploading resume</span>
            <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
          
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Extracting information</span>
            <div className="animate-pulse">
              <div className="h-2 w-2 bg-purple-400 rounded-full"></div>
            </div>
          </div>
          
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Creating profile</span>
            <div className="h-5 w-5 border-2 border-gray-200 rounded-full"></div>
          </div>
        </div>
        
        <div className="mt-8 text-center">
          <p className="text-xs text-gray-500">
            This usually takes 30-60 seconds. Please don't close this page.
          </p>
        </div>
      </div>
    </div>
  );

  const renderReviewStep = () => (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Review Your AI-Generated Profile</h2>
        <p className="text-gray-600">Please review and edit the information extracted from your resume</p>
      </div>

      {aiProfileData && (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 space-y-6 mb-6">
          {/* Personal Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input
                  type="text"
                  value={aiProfileData.contact_info?.first_name || ''}
                  onChange={(e) => setAiProfileData({
                    ...aiProfileData,
                    contact_info: { ...aiProfileData.contact_info, first_name: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input
                  type="text"
                  value={aiProfileData.contact_info?.last_name || ''}
                  onChange={(e) => setAiProfileData({
                    ...aiProfileData,
                    contact_info: { ...aiProfileData.contact_info, last_name: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={aiProfileData.contact_info?.email || ''}
                  onChange={(e) => setAiProfileData({
                    ...aiProfileData,
                    contact_info: { ...aiProfileData.contact_info, email: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  value={aiProfileData.contact_info?.phone || ''}
                  inputMode="numeric"
                  onKeyDown={(e) => {
                   // Allow digits
                   if (/^[0-9]$/.test(e.key)) return;
                   const allowedKeys = [
                    "Backspace",
                    "Delete",
                    "Tab",
                    "ArrowLeft",
                    "ArrowRight",
                    "Home",
                    "End",
                  ];

                 if (allowedKeys.includes(e.key)) return;

                     e.preventDefault();
                  }}
                  onChange={(e) => setAiProfileData({
                    ...aiProfileData,
                    contact_info: { ...aiProfileData.contact_info, phone: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <input
                  type="text"
                  value={aiProfileData.contact_info?.address || (aiProfileData.contact_info?.city && aiProfileData.contact_info?.state ? `${aiProfileData.contact_info.city}, ${aiProfileData.contact_info.state}` : aiProfileData.contact_info?.city || '')}
                  onChange={(e) => setAiProfileData({
                    ...aiProfileData,
                    contact_info: { ...aiProfileData.contact_info, address: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>
          </div>

          {/* Professional Summary */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Professional Summary</h3>
            <textarea
              rows={4}
              value={aiProfileData.professional_summary?.summary || aiProfileData.professional_summary?.objective || ''}
              onChange={(e) => setAiProfileData({
                ...aiProfileData,
                professional_summary: { ...aiProfileData.professional_summary, summary: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          {/* Current Position */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Position</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
                <input
                  type="text"
                  value={aiProfileData.work_experience?.[0]?.job_title || ''}
                  onChange={(e) => {
                    const updated = [...(aiProfileData.work_experience || [{ job_title: '', company_name: '' }])];
                    updated[0] = { ...updated[0], job_title: e.target.value };
                    setAiProfileData({ ...aiProfileData, work_experience: updated });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                <input
                  type="text"
                  value={aiProfileData.work_experience?.[0]?.company_name || ''}
                  onChange={(e) => {
                    const updated = [...(aiProfileData.work_experience || [{ job_title: '', company_name: '' }])];
                    updated[0] = { ...updated[0], company_name: e.target.value };
                    setAiProfileData({ ...aiProfileData, work_experience: updated });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>
          </div>

          {/* Skills */}
          {aiProfileData.skills && aiProfileData.skills.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Skills</h3>
              <div className="flex flex-wrap gap-2">
                {aiProfileData.skills.slice(0, 15).map((skill, index) => (
                  <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {safeRender(skill.name)}
                  </span>
                ))}
                {aiProfileData.skills.length > 15 && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                    +{aiProfileData.skills.length - 15} more
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Education */}
          {aiProfileData.education && aiProfileData.education.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Education</h3>
              <div className="space-y-3">
                {aiProfileData.education.map((edu, index) => (
                  <div key={index} className="border-l-2 border-emerald-200 pl-4">
                    <p className="font-medium text-gray-900">{safeRender(edu.degree_name)}</p>
                    <p className="text-gray-700">{safeRender(edu.institution_name)}</p>
                    <p className="text-sm text-gray-600">{safeRender(edu.graduation_year)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end">
        <button
          onClick={handleConfirmProfile}
          disabled={isProcessing}
          className="flex items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50"
        >
          {isProcessing ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              Confirm & Create Profile
              <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </>
          )}
        </button>
      </div>
    </div>
  );

  const renderSuccessStep = () => (
    <div className="text-center max-w-2xl mx-auto">
      <div className="mb-8">
        <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-green-100 mb-6">
          <svg className="h-10 w-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          🎉 Profile Created Successfully!
        </h1>
        <p className="text-xl text-gray-600 mb-8">
          Your professional profile is now live and ready to be discovered by top companies
        </p>
      </div>

      <div className="bg-gradient-to-r from-emerald-50 to-blue-50 rounded-xl p-8 mb-8">
        <div className="grid md:grid-cols-3 gap-6 text-center">
          <div>
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-emerald-100 mb-4">
              <svg className="h-6 w-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Discoverable</h3>
            <p className="text-sm text-gray-600">Your profile is now visible to recruiters searching for talent like you</p>
          </div>
          
          <div>
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
              <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2H9a2 2 0 00-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Analytics</h3>
            <p className="text-sm text-gray-600">Track profile views, search appearances, and engagement metrics</p>
          </div>
          
          <div>
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-purple-100 mb-4">
              <svg className="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Notifications</h3>
            <p className="text-sm text-gray-600">Get notified when companies view your profile or show interest</p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <button
          onClick={() => {
            sessionStorage.setItem('profile_creation_completed', 'true');
            clearProcessingState();
            navigate('/candidate/dashboard', { replace: true });
          }}
          className="w-full flex justify-center py-4 px-6 border border-transparent rounded-lg shadow-sm text-lg font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition duration-150 ease-in-out"
        >
          Go to Dashboard
        </button>
        
        <p className="text-sm text-gray-600">
          Welcome to the TalentKey.AI community! 🚀
        </p>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'welcome':
        return renderWelcomeStep();
      case 'upload':
        return renderUploadStep();
      case 'processing':
        return renderProcessingStep();
      case 'review':
        return renderReviewStep();
      case 'success':
        return renderSuccessStep();
      default:
        return renderWelcomeStep();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Progress Bar */}
        {currentStep !== 'success' && (
          <div className="mb-8">
            <div className="flex items-center justify-center space-x-2">
              {['welcome', 'upload', 'processing', 'review'].map((step, index) => {
                const stepNames = ['Welcome', 'Upload', 'Processing', 'Review'];
                const isCurrentStep = step === currentStep;
                const isCompleted = ['welcome', 'upload', 'processing', 'review'].indexOf(currentStep) > index;
                
                return (
                  <div key={step} className="flex items-center">
                    <div className={`
                      flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium
                      ${isCurrentStep 
                        ? 'bg-emerald-600 text-white' 
                        : isCompleted 
                          ? 'bg-emerald-600 text-white' 
                          : 'bg-gray-200 text-gray-600'
                      }
                    `}>
                      {isCompleted ? (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        index + 1
                      )}
                    </div>
                    <span className={`ml-2 text-sm font-medium ${isCurrentStep ? 'text-emerald-600' : 'text-gray-500'}`}>
                      {stepNames[index]}
                    </span>
                    {index < 3 && (
                      <div className={`w-12 h-0.5 mx-4 ${isCompleted ? 'bg-emerald-600' : 'bg-gray-200'}`} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {renderCurrentStep()}
      </div>

      <Toast
        message={toastMessage.message}
        type={toastMessage.type}
        isVisible={toastMessage.isVisible}
        onClose={hideToast}
      />
    </div>
  );
};

export default CandidateOnboarding; 